/* eslint-disable */
/**
 * PricingSummaryCalculator — Pure calculation engine for Pricing Summary metrics.
 *
 * Mirrors the exact SQL calculation chain:
 *   vTierBase → vPricingDetailListSubTotalsExtended → vPricingDetailListTotalsExtended → pReport_PricingSummaryExtended → RDL expressions
 *
 * No UI, no side effects, no mutations. Takes tier data + pricing detail data, returns display-ready values.
 */

import moneyHelper from '../Helpers/moneyHelper';
import mathHelper from '../Helpers/mathHelper';
import TierListForOverallHelper from '../Helpers/ModelHelpers/TierListForOverallHelper';

// ─── Input types ───────────────────────────────────────────────────────────────

/** Minimal tier fields needed for calculation (maps to TierItem properties) */
export interface TierInput {
  NumEvalsPerCycle: number;
  ClientFeePerEval: number;
  EvalFeePerEval: number;
  TravelCostPerCycle: number;
  MarkUpPct: number;
  MarkUpPerEval: number;
  /** Pre-computed MarkedUpFeePerEval from TierItem.calculate() */
  MarkedUpFeePerEval: number;
  /** Pre-computed MarkedUpFeePerCycle from TierItem.calculate() */
  MarkedUpFeePerCycle: number;
  /** Pre-computed MarkedUpAnnually from TierItem.calculate() */
  MarkedUpAnnually: number;
  /** Pre-computed ClientFeeAnnually from TierItem.calculate() */
  ClientFeeAnnually: number;
  /** Pre-computed EvalFeeAnnually from TierItem.calculate() */
  EvalFeeAnnually: number;
  /** Pre-computed TravelCostAnnually from TierItem.calculate() */
  TravelCostAnnually: number;
}

/** Minimal pricing detail fields needed for calculation (maps to VPricingDetailBaseItem properties) */
export interface PricingDetailInput {
  NumCyclesPerYr: number;
  OfficeId: number | string;
  OpportunityOfficeId: number | string;
  CurrencyRate: number;
  CurrencyCode: string;
  StatusCode: number;
  FinalizedRate: number;

  // Evaluator costs
  EvalBonusPerCycle: number;
  BankChargesPerCycle: number;

  // Editing costs
  EditorCostPerEval: number;
  TransCostPerEval: number;
  ExtDataCheckPerEval: number;
  OwningOfficeIsEditing: boolean;
  OwningOfficeIsTranslating: boolean;
  OwningOfficeIsChecking: boolean;

  // Employee costs — daily cost (resolved from override or office default)
  TeamLeadDailyCost: number;
  ProjMgrDailyCost: number;
  OperationsMgrDailyCost: number;
  AssistantProjMgrDailyCost: number;
  PanelMgrRecruiterDailyCost: number;
  EditDataCheckDailyCost: number;

  // Employee days per cycle
  TeamLeadDaysPerCycle: number;
  ProjMgrDaysPerCycle: number;
  OperationsMgrDaysPerCycle: number;
  AssistantProjMgrDaysPerCycle: number;
  PanelMgrRecruiterDaysPerCycle: number;
  EditDataCheckDaysPerCycle: number;

  // Employee rates
  PayrollTaxPct: number;
  EmpBenefitsDailyCost: number; // This is a percentage despite the name
  CommissionPct: number;
  OverheadRatePct: number;

  // Other direct costs
  SMCostPerEval: number;
  VocazillaCostPerEval: number;
  IvrfeeCostPerEval: number;
  IcrfeeCostPerEval: number;
  AnnualTestcastorCost: number;
  AnnualCallsToShopperCost: number;
  AnnualRecruitingCost: number;

  // Misc costs
  AnnualMiscCost3: number;
  MiscCostPerCycle1: number;
  MiscCostPerCycle2: number;
  MiscCostPerCycle3: number;

  // Video costs
  LengthVideoShopHours: number;
  EditorCostPerHour: number;
  TimeCaptureCostPerHour: number;
  ShippingCostPerEval: number;
  CardCostPerEval: number;
  StreamingCostPerHour: number;
  CdrcostPerEval: number;
  DvdcostPerEval: number;
  IsVideoCdr: boolean;
  IsVideoDvd: boolean;
  VideoCaseTypeCode: number | string;
  CaseCostPerEval1: number;
  CaseCostPerEval2: number;
  MailingCostPerEval: number;
  Dnepct: number;

  // Pricing-level fields
  OverrideFee: boolean;
  /** When true, the client fee per eval is rounded to a whole currency unit (drives Pricing.FeeBase). */
  RoundFee: boolean;
  PricingFeeBase: number;

  // Currency
  OpportunityCurrencyRate: number;
  OpportunityCurrencyCode: string;

  // Service type (for deferred cost logic)
  ServiceTypeCode: number;
}

// ─── Intermediate result types ─────────────────────────────────────────────────

/** Output of Step 1: tier aggregation (mirrors vPricingDetailListSubTotalsExtended) */
export interface TierAggregates {
  NumEvalsTotal: number;
  SumNumEvalsPerCycle: number;
  BaseClientFeeTotal: number;       // = ClientRevenueBase
  ClientFeeTotal: number;           // = ClientRevenueTotal (MarkedUp)
  MarkedUpRevenuePerCycleTotal: number;
  MarkedUpAnnuallyTotal: number;    // = MarkedUpRevenueTotal
  TierEvalCostTotal: number;        // Just the tier-summed eval costs (before bonus/bank)
  TravelCostTotal: number;
}

/** Output of Step 2: all cost categories (rest of vPricingDetailListSubTotalsExtended) */
export interface CostBreakdown {
  EvalCostTotal: number;
  TravelCostTotal: number;
  RecruitingCostTotal: number;      // = AnnualRecruitingCost from PD (the view uses this directly)
  EditingCostTotal: number;
  EmployeeCostTotal: number;
  OtherCostTotal: number;
  VideoCostTotal: number;
  DeferredCostTotal: number;
  DeferredEmployeeCost: number;
  DeferredOtherCost: number;
  OtherMiscCostTotal: number;
}

/** Output of Step 3: fieldwork totals (mirrors vPricingDetailListTotalsExtended) */
export interface FieldworkTotals {
  FieldWorkCostTotal: number;
  FieldWorkRevenueTotal: number;
  CostTotal: number;
  OwningOfficeCostTotal: number;
  ClientFeeBase: number;
  ClientFeeTotal: number;
}

/** Output of Step 4: report metrics (mirrors pReport_PricingSummaryExtended) */
export interface ReportMetrics {
  Commission: number;
  OldCommission: number;
  NewOtherCostTotal: number;
  FWEmployeeCost: number;
  FWOtherDirectCost: number;
  FWEvalCostTotal: number;
  NewCostTotal: number;
  IsSameOffice: boolean;
  ClientFeeMarkup: number;
}

// ─── Final display result ──────────────────────────────────────────────────────

export interface PricingSummaryDisplayValues {
  // Fieldwork Office section
  fwRevenue: number;
  fwCost: number;
  fwNetRevenue: number;
  fwNetRevenuePct: string;
  fwCostPct: string;

  // Owning Office section
  ooRevenue: number;
  ooCost: number;
  ooNetRevenue: number;
  ooNetRevenuePct: string;
  ooCostPct: string;
  ooMarkupPct: number;

  // Overall (combined) — only meaningful when IsSameOffice = false
  overallNetRevenue: number;
  overallNetRevenuePct: string;

  // Metadata
  isSameOffice: boolean;
  currencyCode: string;

  // Formatted strings
  fwRevenueFormatted: string;
  fwCostFormatted: string;
  fwNetRevenueFormatted: string;
  ooRevenueFormatted: string;
  ooCostFormatted: string;
  ooNetRevenueFormatted: string;
  overallNetRevenueFormatted: string;
}

/** Cross-pricing overall totals (all pricings in opportunity, converted to opportunity currency) */
export interface OverallTotals {
  revenueTotal: number;
  costTotal: number;
  netRevenueTotal: number;
  costPct: string;
  revenuePct: string;

  // FW office totals (in pricing currency of current pricing)
  fwRevenueTotal: number;
  fwCostTotal: number;
  fwNetRevenueTotal: number;
  fwCostPct: string;
  fwNetRevenuePct: string;

  isSameOffice: boolean;
  opportunityCurrencyCode: string;
  pricingCurrencyCode: string;

  // Formatted
  revenueTotalFormatted: string;
  costTotalFormatted: string;
  netRevenueTotalFormatted: string;
  fwRevenueTotalFormatted: string;
  fwCostTotalFormatted: string;
  fwNetRevenueTotalFormatted: string;
}

// ─── Helper: safe number parsing ───────────────────────────────────────────────

function n(value: any): number {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function getVideoCaseCost(videoCaseTypeCode: number | string, caseCost1: number, caseCost2: number): number {
  const code = parseInt(String(videoCaseTypeCode));
  if (code === 1) return n(caseCost1);
  if (code === 2) return n(caseCost2);
  return 0;
}

// ─── Main Calculator ───────────────────────────────────────────────────────────

export default class PricingSummaryCalculator {

  /**
   * Step 1: Aggregate tier-level sums.
   * Mirrors the subquery in vPricingDetailListSubTotalsExtended that groups by PricingDetailId.
   */
  static computeTierAggregates(tiers: TierInput[], pd: PricingDetailInput): TierAggregates {
    let numEvalsTotal = 0;
    let sumNumEvalsPerCycle = 0;
    let baseClientFeeTotal = 0;
    let markedUpWithPerEvalAnnual = 0; // Σ (MarkedUpFeePerEval + MarkUpPerEval) * evals — feeds FeeBase
    let tierEvalCostTotal = 0;
    let travelCostTotal = 0;
    let markedUpRevenuePerCycleTotal = 0;
    let markedUpAnnuallyTotal = 0;

    const numCycles = n(pd.NumCyclesPerYr);

    for (const t of tiers) {
      const nEvals = n(t.NumEvalsPerCycle);
      const markedUpFeePerEval = n(t.MarkedUpFeePerEval);
      sumNumEvalsPerCycle += nEvals;
      numEvalsTotal += nEvals * numCycles;

      // BaseClientFeeTotal = sum(NumEvalsPerCycle * ClientFeePerEval * NumCyclesPerYr)
      baseClientFeeTotal += nEvals * n(t.ClientFeePerEval) * numCycles;

      // Accumulate the per-eval fee the way pPricing_CalculateFees does for FeeBase:
      //   ClientFeePerEval * (1 + MarkUpPct/100) + MarkUpPerEval  ==  MarkedUpFeePerEval + MarkUpPerEval
      markedUpWithPerEvalAnnual += (markedUpFeePerEval + n(t.MarkUpPerEval)) * nEvals * numCycles;

      tierEvalCostTotal += nEvals * n(t.EvalFeePerEval) * numCycles;
      travelCostTotal += n(t.TravelCostPerCycle) * numCycles;
      markedUpRevenuePerCycleTotal += markedUpFeePerEval * nEvals;
      markedUpAnnuallyTotal += markedUpFeePerEval * nEvals * numCycles;
    }

    // ClientFeeTotal (owning revenue) = sum(NumEvalsPerCycle * NumCyclesPerYr * Pricing.FeeBase).
    // CD-5275: re-derive FeeBase LIVE from the tier data instead of summing MarkedUpFeePerEval, so the
    // box reflects unsaved fee/markup edits AND matches the report.  This mirrors pPricing_CalculateFees:
    // FeeBase = the eval-weighted average marked-up fee per eval (incl. MarkUpPerEval), rounded to a whole
    // currency unit when the pricing has RoundFee, then to 2dp; owning revenue = FeeBase * NumEvalsTotal.
    // MarkedUpFeePerEval does NOT apply RoundFee, so the old approach understated revenue whenever RoundFee
    // was set — e.g. opp 18129 showed USD 218,330.35 in the box vs 218,405.91 in the report (a 75.56 gap).
    // OverrideFee pricings encode the manual fee in MarkedUpFeePerEval (via a recomputed MarkUpPct) and are
    // not whole-rounded, so they keep the raw marked-up total. The cost side always uses MarkedUpFeePerEval
    // (MarkedUpAnnuallyTotal / MarkedUpRevenuePerCycleTotal), matching the report's cost calculations.
    let clientFeeTotal: number;
    if (pd.OverrideFee) {
      clientFeeTotal = markedUpAnnuallyTotal;
    } else {
      const feePerEval = numEvalsTotal === 0 ? 0 : markedUpWithPerEvalAnnual / numEvalsTotal;
      const feeBase = round2(pd.RoundFee ? Math.round(feePerEval) : feePerEval);
      clientFeeTotal = feeBase * numEvalsTotal;
    }

    return {
      NumEvalsTotal: round2(numEvalsTotal),
      SumNumEvalsPerCycle: round2(sumNumEvalsPerCycle),
      BaseClientFeeTotal: round2(baseClientFeeTotal),
      ClientFeeTotal: round2(clientFeeTotal),
      TierEvalCostTotal: round2(tierEvalCostTotal),
      TravelCostTotal: round2(travelCostTotal),
      MarkedUpRevenuePerCycleTotal: round2(markedUpRevenuePerCycleTotal),
      MarkedUpAnnuallyTotal: round2(markedUpAnnuallyTotal),
    };
  }

  /**
   * Step 2: Compute all cost categories.
   * Mirrors the SELECT columns in vPricingDetailListSubTotalsExtended (outside the subquery).
   */
  static computeCostBreakdown(agg: TierAggregates, pd: PricingDetailInput): CostBreakdown {
    const numCycles = n(pd.NumCyclesPerYr);
    const numEvalsTotal = agg.NumEvalsTotal;
    const currencyRate = n(pd.CurrencyRate);

    // ── EvalCostTotal ──
    // SQL: Round(pd.EvalCostTotal + base.NumCyclesPerYr * (base.EvalBonusPerCycle + base.BankChargesPerCycle), 2)
    const evalCostTotal = round2(
      agg.TierEvalCostTotal + numCycles * (n(pd.EvalBonusPerCycle) + n(pd.BankChargesPerCycle))
    );

    // ── TravelCostTotal ──
    const travelCostTotal = round2(agg.TravelCostTotal);

    // ── RecruitingCostTotal ── (SQL just uses AnnualRecruitingCost directly)
    const recruitingCostTotal = round2(n(pd.AnnualRecruitingCost));

    // ── EditingCostTotal ──
    // SQL: Round(pd.NumEvalsTotal * (EditorCost if !OwningOfficeIsEditing + TransCost if !OwningOfficeIsTranslating + ExtDataCheck if !OwningOfficeIsChecking), 2)
    const editingCostTotal = round2(
      numEvalsTotal *
        ((pd.OwningOfficeIsEditing ? 0 : n(pd.EditorCostPerEval)) +
          (pd.OwningOfficeIsTranslating ? 0 : n(pd.TransCostPerEval)) +
          (pd.OwningOfficeIsChecking ? 0 : n(pd.ExtDataCheckPerEval)))
    );

    // ── EmployeeCostTotal ──
    // SQL: Round(NumCyclesPerYr * (1 + PayrollTaxPct/100 + EmpBenefitsDailyCost/100) * CurrencyRate *
    //   (sum of DailyCost * DaysPerCycle for each role) + (NumCyclesPerYr * CommissionPct/100 * MarkedUpRevenuePerCycleTotal), 2)
    const dailyCostSum =
      n(pd.TeamLeadDailyCost) * n(pd.TeamLeadDaysPerCycle) +
      n(pd.ProjMgrDailyCost) * n(pd.ProjMgrDaysPerCycle) +
      n(pd.OperationsMgrDailyCost) * n(pd.OperationsMgrDaysPerCycle) +
      n(pd.AssistantProjMgrDailyCost) * n(pd.AssistantProjMgrDaysPerCycle) +
      n(pd.PanelMgrRecruiterDailyCost) * n(pd.PanelMgrRecruiterDaysPerCycle) +
      n(pd.EditDataCheckDailyCost) * n(pd.EditDataCheckDaysPerCycle);

    const employeeCostTotal = round2(
      numCycles *
        (1 + n(pd.PayrollTaxPct) / 100.0 + n(pd.EmpBenefitsDailyCost) / 100.0) *
        currencyRate *
        dailyCostSum +
        numCycles * (n(pd.CommissionPct) / 100.0) * agg.MarkedUpRevenuePerCycleTotal
    );

    // ── DeferredCostTotal ──
    // SQL: Round((CommissionPct/100 * ClientFeeTotal) +
    //   NumEvalsTotal * (CurrencyRate * (SM + Vocazilla + IVR + ICR) +
    //     CurrencyRate * (Editor if OwningOffice + Trans if OwningOffice + ExtDataCheck if OwningOffice)) +
    //   video DNE if applicable, 2)
    const deferredEmployeeCost = round2((n(pd.CommissionPct) / 100.0) * agg.ClientFeeTotal);

    const deferredOtherCost = round2(
      numEvalsTotal *
        (currencyRate *
          (n(pd.SMCostPerEval) + n(pd.VocazillaCostPerEval) + n(pd.IvrfeeCostPerEval) + n(pd.IcrfeeCostPerEval)) +
          currencyRate *
            ((pd.OwningOfficeIsEditing ? n(pd.EditorCostPerEval) : 0) +
              (pd.OwningOfficeIsTranslating ? n(pd.TransCostPerEval) : 0) +
              (pd.OwningOfficeIsChecking ? n(pd.ExtDataCheckPerEval) : 0))) +
        (n(pd.LengthVideoShopHours) > 0
          ? (n(pd.Dnepct) / 100.0) * (agg.ClientFeeTotal - agg.BaseClientFeeTotal)
          : 0)
    );

    const deferredCostTotal = round2(deferredEmployeeCost + deferredOtherCost);

    // ── OtherCostTotal ──
    // SQL: Round((NumEvalsTotal * CurrencyRate * (SM + Vocazilla + IVR + ICR)) +
    //   AnnualMiscCost3 + NumCyclesPerYr * (MiscCostPerCycle1 + MiscCostPerCycle2 + MiscCostPerCycle3) +
    //   AnnualTestcastorCost + AnnualCallsToShopperCost + BaseClientFeeTotal * (OverheadRatePct/100), 2)
    const otherCostTotal = round2(
      numEvalsTotal *
        currencyRate *
        (n(pd.SMCostPerEval) + n(pd.VocazillaCostPerEval) + n(pd.IvrfeeCostPerEval) + n(pd.IcrfeeCostPerEval)) +
        n(pd.AnnualMiscCost3) +
        numCycles * (n(pd.MiscCostPerCycle1) + n(pd.MiscCostPerCycle2) + n(pd.MiscCostPerCycle3)) +
        n(pd.AnnualTestcastorCost) +
        n(pd.AnnualCallsToShopperCost) +
        agg.BaseClientFeeTotal * (n(pd.OverheadRatePct) / 100.0)
    );

    // ── OtherMiscCostTotal ──
    // SQL: Round(AnnualRecruitingCost + AnnualCallsToShopperCost + AnnualMiscCost3 +
    //   (MiscCostPerCycle1 + MiscCostPerCycle2 + MiscCostPerCycle3) * NumCyclesPerYr, 2)
    const otherMiscCostTotal = round2(
      n(pd.AnnualRecruitingCost) +
        n(pd.AnnualCallsToShopperCost) +
        n(pd.AnnualMiscCost3) +
        (n(pd.MiscCostPerCycle1) + n(pd.MiscCostPerCycle2) + n(pd.MiscCostPerCycle3)) * numCycles
    );

    // ── VideoCostTotal ──
    let videoCostTotal = 0;
    if (n(pd.LengthVideoShopHours) > 0) {
      const videoHours = n(pd.LengthVideoShopHours);
      const caseCost = getVideoCaseCost(pd.VideoCaseTypeCode, pd.CaseCostPerEval1, pd.CaseCostPerEval2);

      videoCostTotal = round2(
        numEvalsTotal *
          (videoHours * (n(pd.EditorCostPerHour) + n(pd.TimeCaptureCostPerHour) + n(pd.StreamingCostPerHour)) +
            n(pd.ShippingCostPerEval) +
            n(pd.MailingCostPerEval) +
            n(pd.CardCostPerEval) +
            caseCost +
            (pd.IsVideoCdr ? n(pd.CdrcostPerEval) : 0) +
            (pd.IsVideoDvd ? n(pd.DvdcostPerEval) : 0)) +
          (n(pd.Dnepct) / 100.0) * (agg.ClientFeeTotal - agg.BaseClientFeeTotal)
      );
    }

    return {
      EvalCostTotal: evalCostTotal,
      TravelCostTotal: travelCostTotal,
      RecruitingCostTotal: recruitingCostTotal,
      EditingCostTotal: editingCostTotal,
      EmployeeCostTotal: employeeCostTotal,
      OtherCostTotal: otherCostTotal,
      VideoCostTotal: videoCostTotal,
      DeferredCostTotal: deferredCostTotal,
      DeferredEmployeeCost: deferredEmployeeCost,
      DeferredOtherCost: deferredOtherCost,
      OtherMiscCostTotal: otherMiscCostTotal,
    };
  }

  /**
   * Step 3: Compute fieldwork totals.
   * Mirrors vPricingDetailListTotalsExtended.
   */
  static computeFieldworkTotals(
    agg: TierAggregates,
    costs: CostBreakdown,
    isOwningOffice: boolean
  ): FieldworkTotals {
    // FieldWorkCostTotal = sum of all costs - deferred (if not owning office)
    // SQL: EvalCostTotal + TravelCostTotal + RecruitingCostTotal + EditingCostTotal +
    //      EmployeeCostTotal + OtherCostTotal + VideoCostTotal - (isOwningOffice ? 0 : DeferredCostTotal)
    const allCostsSum =
      costs.EvalCostTotal +
      costs.TravelCostTotal +
      costs.RecruitingCostTotal +
      costs.EditingCostTotal +
      costs.EmployeeCostTotal +
      costs.OtherCostTotal +
      costs.VideoCostTotal;

    const fieldWorkCostTotal = round2(allCostsSum - (isOwningOffice ? 0 : costs.DeferredCostTotal));

    const fieldWorkRevenueTotal = round2(
      isOwningOffice ? agg.MarkedUpAnnuallyTotal : agg.BaseClientFeeTotal
    );

    // CostTotal:
    // isOwningOffice=1 (same office): all costs sum (no deferred subtraction)
    // isOwningOffice=0 (different office): ClientRevenueBase + DeferredCostTotal
    const costTotal = round2(isOwningOffice ? allCostsSum : agg.BaseClientFeeTotal + costs.DeferredCostTotal);

    const owningOfficeCostTotal = round2(isOwningOffice ? 0 : costs.DeferredCostTotal);

    const clientFeeBase = round2(agg.NumEvalsTotal === 0 ? 0 : agg.BaseClientFeeTotal / agg.NumEvalsTotal);
    const clientFeeTotal = round2(agg.NumEvalsTotal === 0 ? 0 : agg.ClientFeeTotal / agg.NumEvalsTotal);

    return {
      FieldWorkCostTotal: fieldWorkCostTotal,
      FieldWorkRevenueTotal: fieldWorkRevenueTotal,
      CostTotal: costTotal,
      OwningOfficeCostTotal: owningOfficeCostTotal,
      ClientFeeBase: clientFeeBase,
      ClientFeeTotal: clientFeeTotal,
    };
  }

  /**
   * Step 4: Compute report-level metrics.
   * Mirrors pReport_PricingSummaryExtended.
   */
  static computeReportMetrics(
    agg: TierAggregates,
    costs: CostBreakdown,
    fw: FieldworkTotals,
    pd: PricingDetailInput
  ): ReportMetrics {
    const numCycles = n(pd.NumCyclesPerYr);
    const isSameOffice = String(pd.OfficeId) === String(pd.OpportunityOfficeId);

    // Commission = CommissionPct/100 * MarkedUpRevenuePerCycleTotal * NumCyclesPerYr
    // Note: The SSRS proc uses ISNULL(ISNULL(s.OverrideCommissionRate, cmr.Rate), 0).
    // The frontend CommissionPct should be the resolved value from fPricingDetail_ExternalCosts.
    // NOT rounded here — the report proc keeps full precision for Commission and NewCostTotal,
    // rounding only happens at the final display/sum level.  Per-row rounding causes cumulative
    // drift when many pricings are converted to opportunity currency (e.g. 0.15 CNY on opp 17836).
    const commission =
      (n(pd.CommissionPct) / 100.0) * agg.MarkedUpRevenuePerCycleTotal * numCycles;

    // OldCommission — uses opportunity-level CommissionPct (not available on frontend, skip for now)
    const oldCommission = 0;

    // NewOtherCostTotal — DIFFERENT from the view's OtherCostTotal!
    // SQL: OverheadRatePct/100 * ClientRevenueBase
    //      + ICR * SumNumEvalsPerCycle * NumCyclesPerYr
    //      + IVR * SumNumEvalsPerCycle * NumCyclesPerYr
    //      + Vocazilla * SumNumEvalsPerCycle * NumCyclesPerYr
    //      + CurrencyRate * SM * SumNumEvalsPerCycle * NumCyclesPerYr
    // Note: In the proc, SM cost uses (CASE WHEN StatusCode=2 THEN FinalizedRate ELSE CurrencyRate END)
    // but our pd.CurrencyRate is already resolved (FinalizedRate when finalized, live rate otherwise)
    // from vPricingDetailBase. The ICR/IVR/Vocazilla come from vPricingBase which doesn't multiply by currencyRate.
    const currencyRate = n(pd.CurrencyRate);
    const smRate = n(pd.StatusCode) === 2 ? n(pd.FinalizedRate) : currencyRate;

    // Not rounded — matches report proc (no ROUND on this expression).
    const newOtherCostTotal =
      (n(pd.OverheadRatePct) / 100.0) * agg.BaseClientFeeTotal +
        n(pd.IcrfeeCostPerEval) * agg.SumNumEvalsPerCycle * numCycles +
        n(pd.IvrfeeCostPerEval) * agg.SumNumEvalsPerCycle * numCycles +
        n(pd.VocazillaCostPerEval) * agg.SumNumEvalsPerCycle * numCycles +
        smRate * n(pd.SMCostPerEval) * agg.SumNumEvalsPerCycle * numCycles;

    // The proc zeroes DeferredOtherCost for same-office pricings:
    //   CASE WHEN ob.OfficeId <> pb.OfficeId THEN pdlt.DeferredOtherCost ELSE 0.00 END
    const effectiveDeferredOtherCost = isSameOffice ? 0 : costs.DeferredOtherCost;

    // FWEmployeeCost = EmployeeCostTotal - Commission  (not rounded in proc)
    const fwEmployeeCost = costs.EmployeeCostTotal - commission;

    // FWOtherDirectCost = NewOtherCostTotal - DeferredOtherCost  (not rounded in proc)
    const fwOtherDirectCost = newOtherCostTotal - effectiveDeferredOtherCost;

    // FWEvalCostTotal = EvalCostTotal + TravelCostTotal  (not rounded in proc)
    const fwEvalCostTotal = costs.EvalCostTotal + costs.TravelCostTotal;

    // NewCostTotal — NOT rounded per row, matching the report proc.
    // The caller (computeOverallTotals) accumulates full-precision values then round2() the final sum,
    // and computeDisplayValues wraps in round2() for per-pricing display.
    // Different office: ClientRevenueBase + Commission + DeferredOtherCost
    // Same office: OtherMiscCostTotal + FWEvalCostTotal + EditingCostTotal + FWEmployeeCost + FWOtherDirectCost + Commission + 0
    let newCostTotal: number;
    if (!isSameOffice) {
      newCostTotal = agg.BaseClientFeeTotal + commission + effectiveDeferredOtherCost;
    } else {
      newCostTotal =
        costs.OtherMiscCostTotal +
          fwEvalCostTotal +
          costs.EditingCostTotal +
          fwEmployeeCost +
          fwOtherDirectCost +
          commission;
    }

    // ClientFeeMarkup
    const clientFeeMarkup =
      agg.BaseClientFeeTotal === 0 ? 0 : (agg.ClientFeeTotal - agg.BaseClientFeeTotal) / agg.BaseClientFeeTotal;

    return {
      Commission: commission,
      OldCommission: oldCommission,
      NewOtherCostTotal: newOtherCostTotal,
      FWEmployeeCost: fwEmployeeCost,
      FWOtherDirectCost: fwOtherDirectCost,
      FWEvalCostTotal: fwEvalCostTotal,
      NewCostTotal: newCostTotal,
      IsSameOffice: isSameOffice,
      ClientFeeMarkup: clientFeeMarkup,
    };
  }

  /**
   * Step 5: Compute final display values.
   * Mirrors the RDL expressions in "Pricing Summary Adjusted.rdl".
   */
  static computeDisplayValues(
    agg: TierAggregates,
    costs: CostBreakdown,
    fw: FieldworkTotals,
    rm: ReportMetrics,
    pd: PricingDetailInput
  ): PricingSummaryDisplayValues {
    const cc = pd.CurrencyCode;

    // ── Fieldwork Office section ──
    const fwRevenue = round2(agg.BaseClientFeeTotal);
    const fwCost = round2(fw.FieldWorkCostTotal);
    const fwNetRevenue = round2(fwRevenue - fwCost);

    // ── Owning Office section ──
    // Revenue = ClientRevenueTotal (= MarkedUpAnnuallyTotal)
    const ooRevenue = round2(agg.ClientFeeTotal);
    const ooCost = round2(rm.NewCostTotal);
    const ooNetRevenue = round2(ooRevenue - ooCost);

    // ── Overall ──
    // If IsSameOffice=0: (ClientRevenueTotal - NewCostTotal) + (ClientRevenueBase - FieldWorkCostTotal)
    // If IsSameOffice=1: ClientRevenueTotal - NewCostTotal
    const overallNetRevenue = rm.IsSameOffice
      ? ooNetRevenue
      : round2((agg.ClientFeeTotal - rm.NewCostTotal) + (agg.BaseClientFeeTotal - fw.FieldWorkCostTotal));

    return {
      fwRevenue,
      fwCost,
      fwNetRevenue,
      fwNetRevenuePct: mathHelper.PercentageText(fwNetRevenue, fwRevenue),
      fwCostPct: mathHelper.PercentageText(fwCost, fwRevenue),

      ooRevenue,
      ooCost,
      ooNetRevenue,
      ooNetRevenuePct: mathHelper.PercentageText(ooNetRevenue, ooRevenue),
      ooCostPct: mathHelper.PercentageText(ooCost, ooRevenue),
      ooMarkupPct: rm.ClientFeeMarkup,

      overallNetRevenue,
      overallNetRevenuePct: mathHelper.PercentageText(overallNetRevenue, ooRevenue),

      isSameOffice: rm.IsSameOffice,
      currencyCode: cc,

      fwRevenueFormatted: moneyHelper.toMoneyFormat(fwRevenue, cc),
      fwCostFormatted: moneyHelper.toMoneyFormat(fwCost, cc),
      fwNetRevenueFormatted: moneyHelper.toMoneyFormat(fwNetRevenue, cc),
      ooRevenueFormatted: moneyHelper.toMoneyFormat(ooRevenue, cc),
      ooCostFormatted: moneyHelper.toMoneyFormat(ooCost, cc),
      ooNetRevenueFormatted: moneyHelper.toMoneyFormat(ooNetRevenue, cc),
      overallNetRevenueFormatted: moneyHelper.toMoneyFormat(overallNetRevenue, cc),
    };
  }

  /**
   * Main entry point: compute all display values for a single pricing detail.
   */
  static compute(tiers: TierInput[], pd: PricingDetailInput): PricingSummaryDisplayValues {
    const agg = PricingSummaryCalculator.computeTierAggregates(tiers, pd);
    const costs = PricingSummaryCalculator.computeCostBreakdown(agg, pd);
    const isOwningOffice = String(pd.OfficeId) === String(pd.OpportunityOfficeId);
    const fw = PricingSummaryCalculator.computeFieldworkTotals(agg, costs, isOwningOffice);
    const rm = PricingSummaryCalculator.computeReportMetrics(agg, costs, fw, pd);
    return PricingSummaryCalculator.computeDisplayValues(agg, costs, fw, rm, pd);
  }

  /**
   * Compute overall (cross-pricing) totals for all pricings in the opportunity.
   *
   * Each element in the array represents one pricing's tiers + its pricing detail.
   * All values are converted to opportunity currency.
   */
  static computeOverallTotals(
    pricings: Array<{ tiers: TierInput[]; pd: PricingDetailInput }>,
    currentPd: PricingDetailInput
  ): OverallTotals {
    let revenueTotal = 0;
    let costTotal = 0;
    let fwRevenueTotal = 0;
    let fwCostTotal = 0;
    const isSameOffice = String(currentPd.OfficeId) === String(currentPd.OpportunityOfficeId);
    const oppCurrCode = currentPd.OpportunityCurrencyCode || '';
    const pricingCurrCode = currentPd.CurrencyCode || '';

    for (const { tiers, pd } of pricings) {
      const agg = PricingSummaryCalculator.computeTierAggregates(tiers, pd);
      const costs = PricingSummaryCalculator.computeCostBreakdown(agg, pd);
      const isOwning = String(pd.OfficeId) === String(pd.OpportunityOfficeId);
      const fw = PricingSummaryCalculator.computeFieldworkTotals(agg, costs, isOwning);
      const rm = PricingSummaryCalculator.computeReportMetrics(agg, costs, fw, pd);

      // Convert to opportunity currency
      const oppRate = n(pd.OpportunityCurrencyRate);
      const curRate = n(pd.CurrencyRate);
      const conversionFactor = curRate === 0 ? 0 : oppRate / curRate;

      revenueTotal += agg.ClientFeeTotal * conversionFactor;
      costTotal += rm.NewCostTotal * conversionFactor;

      // FW totals: only for pricings in the current pricing's fieldwork office
      if (String(pd.OfficeId) === String(currentPd.OfficeId)) {
        // Convert to pricing currency of current pricing detail
        const pricingConv = n(currentPd.CurrencyRate) / (curRate === 0 ? 1 : curRate);
        fwRevenueTotal += agg.BaseClientFeeTotal * pricingConv;
        fwCostTotal += fw.FieldWorkCostTotal * pricingConv;
      }
    }

    revenueTotal = round2(revenueTotal);
    costTotal = round2(costTotal);
    const netRevenueTotal = round2(revenueTotal - costTotal);
    fwRevenueTotal = round2(fwRevenueTotal);
    fwCostTotal = round2(fwCostTotal);
    const fwNetRevenueTotal = round2(fwRevenueTotal - fwCostTotal);

    return {
      revenueTotal,
      costTotal,
      netRevenueTotal,
      costPct: mathHelper.PercentageText(costTotal, revenueTotal),
      revenuePct: mathHelper.PercentageText(netRevenueTotal, revenueTotal),

      fwRevenueTotal,
      fwCostTotal,
      fwNetRevenueTotal,
      fwCostPct: mathHelper.PercentageText(fwCostTotal, fwRevenueTotal),
      fwNetRevenuePct: mathHelper.PercentageText(fwNetRevenueTotal, fwRevenueTotal),

      isSameOffice,
      opportunityCurrencyCode: oppCurrCode,
      pricingCurrencyCode: pricingCurrCode,

      revenueTotalFormatted: moneyHelper.toMoneyFormat(revenueTotal, oppCurrCode),
      costTotalFormatted: moneyHelper.toMoneyFormat(costTotal, oppCurrCode),
      netRevenueTotalFormatted: moneyHelper.toMoneyFormat(netRevenueTotal, oppCurrCode),
      fwRevenueTotalFormatted: moneyHelper.toMoneyFormat(fwRevenueTotal, pricingCurrCode),
      fwCostTotalFormatted: moneyHelper.toMoneyFormat(fwCostTotal, pricingCurrCode),
      fwNetRevenueTotalFormatted: moneyHelper.toMoneyFormat(fwNetRevenueTotal, pricingCurrCode),
    };
  }
}
